import { type RefObject, useCallback, useRef, useState } from 'react';

import ErrorBoundary from 'components/ErrorBoundary';
import { GlobalStoreSelectorButton } from 'components/GlobalStoreSelector';
import IconButton from 'components/IconButton';
import JulaLogo from 'components/JulaLogo';
import { LayoutContainer } from 'components/Layout';
import Link from 'components/Link';
import UserProfileButton from 'components/UserProfileButton';
import {
	HEADER_MAIN_NAVIGATION_ID,
	HEADER_SEARCH_FIELD_ID,
	HEADER_SEARCH_FIELD_MOBILE_ID,
} from 'constants/ids';
import { useUserContext } from 'contexts';
import {
	useFeatureToggle,
	useGlobalLinks,
	useIsCompanySite,
	useIsStickyStuck,
	useMinWidth,
	useValueChangeEffect,
} from 'hooks';
import { cn } from 'utils/classNames';
import { goToElement } from 'utils/dom';
import { getTestDataAttrFrom, is } from 'utils/helpers';
import { useI18n } from 'utils/i18n';

import PageHeaderSearch from './PageHeaderSearch';

interface Props {
	accountMenuButtonLabel: string;
	cartButtonLabel: string;
	/** Number of products in the cart */
	cartQuantity?: number;

	/** If the user is logged in */
	isAuthenticatedUser?: boolean;

	/** If the user is being initialised */
	isInitialisingUser?: boolean;

	/** If the viewport is scrolled past the entire header */
	isScrolledPastHeader: boolean;

	/** If the small screen navigation menu is open */
	isToggledMenuOpen: boolean;

	/** Small screen navigation menu toggle button ref */
	menuToggleRef: RefObject<HTMLButtonElement | null>;

	mobileMenuButtonLabel: string;

	/** Click handler for logged in account button */
	onAccountClick: () => void;

	/** Click handler for login button */
	onLoginClick: () => void;

	/** Click handler for the small screen navigation menu button */
	onMenuToggleClick: () => void;

	/** Mousedown handler for the small screen navigation menu button */
	onMenuToggleMouseDown: () => void;
	/** Click handler for the store selector on small screens */
	onStoreSelectorClick: () => void;
	popularProductsText: string;
	popularSearchTermsText: string;
	searchBrandSuggestionLabel: string;
	searchCancelButtonText: string;
	searchCategorySuggestionLabel: string;
	searchFieldLabel: string;
	searchFieldPlaceholderText: string;
	searchInspirationSuggestionLabel: string;
	searchNoResultsText: string;
	searchOpenSearchButtonLabel: string;
	searchOtherSearchTermRecommendationsText: string;
	searchResultProductsText: string;
	searchResultsSpellingSuggestionText: string;
	searchResultsText: string;
	searchStoreSuggestionLabel: string;
	searchSuggestionLabel: string;
	wishlistButtonLabel: string;
	/** Wishlist product quantity */
	wishlistQuantity?: number;
}

/** Header main part with logo, search and icon buttons */
export default function PageHeaderMain({
	accountMenuButtonLabel,
	cartButtonLabel,
	cartQuantity,
	isAuthenticatedUser = false,
	isInitialisingUser = false,
	isScrolledPastHeader,
	isToggledMenuOpen,
	menuToggleRef,
	mobileMenuButtonLabel,
	onAccountClick,
	onLoginClick,
	onMenuToggleClick,
	onMenuToggleMouseDown,
	onStoreSelectorClick,
	popularProductsText,
	popularSearchTermsText,
	searchBrandSuggestionLabel,
	searchCancelButtonText,
	searchCategorySuggestionLabel,
	searchFieldLabel,
	searchFieldPlaceholderText,
	searchInspirationSuggestionLabel,
	searchNoResultsText,
	searchOpenSearchButtonLabel,
	searchOtherSearchTermRecommendationsText,
	searchResultProductsText,
	searchResultsSpellingSuggestionText,
	searchResultsText,
	searchStoreSuggestionLabel,
	searchSuggestionLabel,
	wishlistButtonLabel,
	wishlistQuantity = 0,
}: Props) {
	const { t, tPlural } = useI18n();
	const { cart, wishlist } = useGlobalLinks();
	const isCompanySite = useIsCompanySite();
	const { firstName, isLoading } = useUserContext();
	const isMinMd = useMinWidth('md');
	const { julaClubEnabled, julaProEnabled, wishlistEnabled } =
		useFeatureToggle();

	const hasWishlistButton = wishlistEnabled && wishlist;
	const hasAccountButton =
		(julaClubEnabled && !isCompanySite) || (julaProEnabled && isCompanySite);

	const runAccountClickCallback = () => {
		if (isAuthenticatedUser) {
			onAccountClick();
		} else {
			onLoginClick();
		}
	};

	// Since there is a single user button for the login panel and account menu,
	// the user info must be loaded to know which callback to trigger (i.e. is
	// the current user logged in or not). If the user presses the button before
	// that info is loaded, set a loading state to show UI feedback and trigger
	// the relevant callback when the info becomes available.
	const [hasVisualAccountLoading, setHasVisualAccountLoading] = useState(false);
	const handleLoginClick = () => {
		if (isInitialisingUser) {
			if (!hasVisualAccountLoading) {
				setHasVisualAccountLoading(true);
			}
			return;
		}
		onLoginClick();
	};
	useValueChangeEffect(isInitialisingUser, (prevIsLoadingUser) => {
		if (prevIsLoadingUser && !isInitialisingUser && hasVisualAccountLoading) {
			runAccountClickCallback();
			setHasVisualAccountLoading(false);
		}
	});

	// Would prefer rem. Each Tailwind unit is 4 px. Match with top/padding diff
	// on container below.
	const [containerRef, isContainerStuck] = useIsStickyStuck<HTMLDivElement>(
		isMinMd ? -12 : -8,
	);

	const openSearchButtonRef = useRef<HTMLButtonElement>(null);
	const isSearchButtonVisible = isScrolledPastHeader;
	const [didUseOpenSearchButton, setDidUseOpenSearchButton] = useState(false);
	const handleOpenSearchClick = () => {
		setDidUseOpenSearchButton(true);
		// Let the 'click outside' logic in the search combobox finish before
		// moving focus to it. Otherwise it will focus → open → this button was
		// a click outside → close.
		setTimeout(() => {
			goToElement(HEADER_SEARCH_FIELD_MOBILE_ID);
		});
	};
	const handleSmallSearchClose = useCallback(() => {
		if (didUseOpenSearchButton) {
			setDidUseOpenSearchButton(false);
			// The combobox handles focus on close in several different ways, let
			// it finish before stealing focus to the button.
			setTimeout(() => {
				// Ensure the button is still available. It could be used to open
				// search and then disappear due to scrolling in the background.
				if (isSearchButtonVisible) {
					openSearchButtonRef.current?.focus();
				}
			}, 30);
		}
	}, [didUseOpenSearchButton, isSearchButtonVisible]);

	return (
		<>
			<div
				ref={containerRef}
				// No page-header-part class here, the stickyPageHeader z-index does
				// what's needed.
				className={cn(
					// Match top/padding with stuck hook above.
					'sticky z-stickyPageHeader bg-base transition-shadow',
					'-top-2 pt-4 pb-2',
					'headerRow:pb-3',
					'max-md:mb-1',
					'md:-top-3 md:pt-6 md:pb-3',
					isContainerStuck && 'shadow-xl forced-colors-outline',
				)}
			>
				<LayoutContainer innerClassName="flex flex-wrap items-center sm:flex-nowrap">
					<Link
						href="/"
						aria-label={[
							t('general_jula_logo_start_page_link_label'),
							isCompanySite && t('company_site_logo_title'),
						]
							.filter(Boolean)
							.join(', ')}
						data-cy={getTestDataAttrFrom('header-logo-link')}
						// Pointless but pretty rounded — match focus outline with logo radius.
						className="inline-block shrink-0 rounded-[8px] sm:mr-8 headerRow:rounded-[10px]"
					>
						<JulaLogo
							title={isCompanySite ? t('company_site_logo_title') : undefined}
						/>
					</Link>

					<ErrorBoundary className="grow self-center max-headerRow:hidden">
						<PageHeaderSearch
							id={HEADER_SEARCH_FIELD_ID}
							dataCy="search-field"
							className="grow max-headerRow:hidden"
							searchBrandSuggestionLabel={searchBrandSuggestionLabel}
							searchCancelButtonText={searchCancelButtonText}
							searchCategorySuggestionLabel={searchCategorySuggestionLabel}
							searchFieldPlaceholderText={searchFieldPlaceholderText}
							searchInspirationSuggestionLabel={
								searchInspirationSuggestionLabel
							}
							searchOtherSearchTermRecommendationsText={
								searchOtherSearchTermRecommendationsText
							}
							popularProductsText={popularProductsText}
							popularSearchTermsText={popularSearchTermsText}
							searchResultProductsText={searchResultProductsText}
							searchResultsText={searchResultsText}
							searchResultsSpellingSuggestionText={
								searchResultsSpellingSuggestionText
							}
							searchFieldLabel={searchFieldLabel}
							searchNoResultsText={searchNoResultsText}
							searchStoreSuggestionLabel={searchStoreSuggestionLabel}
							searchSuggestionLabel={searchSuggestionLabel}
						/>
					</ErrorBoundary>

					<div
						// Pull right edge to visually align icon.
						className="-mr-3 ml-auto flex max-headerRow:-my-1 min-[390px]:gap-1 sm:gap-2 headerRow:ml-5"
					>
						<IconButton
							ref={openSearchButtonRef}
							className={cn(
								'transition-[visibility,opacity] max-[350px]:hidden headerRow:hidden',
								!isSearchButtonVisible && 'invisible opacity-0',
							)}
							icon="search"
							text={searchOpenSearchButtonLabel}
							onClick={handleOpenSearchClick}
						/>
						{hasAccountButton && (
							<>
								{isAuthenticatedUser && (
									<UserProfileButton
										onClick={onAccountClick}
										aria-haspopup="dialog"
										firstName={firstName}
										isLoading={isLoading}
										label={accountMenuButtonLabel}
										data-cy={getTestDataAttrFrom(
											'page-header-my-account-button',
										)}
									/>
								)}
								{!isAuthenticatedUser && (
									<IconButton
										onClick={handleLoginClick}
										aria-haspopup="dialog"
										icon="user"
										isLoading={hasVisualAccountLoading}
										text={
											isInitialisingUser
												? t('general_loading_text')
												: t('general_login_button_text')
										}
										data-cy={getTestDataAttrFrom('page-header-login-button')}
									/>
								)}
							</>
						)}
						{hasWishlistButton && (
							<IconButton
								icon="shopping-list"
								href={wishlist}
								text={
									is.positiveNumber(wishlistQuantity)
										? `${wishlistButtonLabel}, ${tPlural('general_amount_of_products', wishlistQuantity)}.`
										: wishlistButtonLabel
								}
								badge={wishlistQuantity || undefined}
								data-cy={getTestDataAttrFrom('page-header-wishlist-button')}
							/>
						)}
						{cart && (
							<IconButton
								icon="cart"
								href={cart}
								text={
									is.positiveNumber(cartQuantity)
										? `${cartButtonLabel}, ${tPlural('general_amount_of_products', cartQuantity)}.`
										: cartButtonLabel
								}
								badge={
									is.positiveNumber(cartQuantity) ? cartQuantity : undefined
								}
								data-cy={getTestDataAttrFrom('page-header-cart')}
							/>
						)}
						<IconButton
							ref={menuToggleRef}
							className="md:hidden"
							icon="menu"
							text={mobileMenuButtonLabel}
							onClick={onMenuToggleClick}
							onMouseDown={onMenuToggleMouseDown}
							aria-haspopup="dialog"
							aria-controls={HEADER_MAIN_NAVIGATION_ID}
							aria-expanded={isToggledMenuOpen}
						/>
					</div>
				</LayoutContainer>

				{/* Internal overlay to isolate search field when that's open, see site.css. */}
				<div className="page-header-overlay" />
			</div>

			<ErrorBoundary className="mx-4 mb-4 block headerRow:hidden">
				<PageHeaderSearch
					id={HEADER_SEARCH_FIELD_MOBILE_ID}
					dataCy="search-field-small"
					// Sneaks along behind the actual sticky header to keep the scroll
					// position when focus moves to it via the 'open search' button.
					className="sticky top-0 z-belowStickyPageHeader mb-4 headerRow:hidden"
					onClose={handleSmallSearchClose}
					searchBrandSuggestionLabel={searchBrandSuggestionLabel}
					searchCancelButtonText={searchCancelButtonText}
					searchCategorySuggestionLabel={searchCategorySuggestionLabel}
					searchFieldPlaceholderText={searchFieldPlaceholderText}
					searchInspirationSuggestionLabel={searchInspirationSuggestionLabel}
					searchOtherSearchTermRecommendationsText={
						searchOtherSearchTermRecommendationsText
					}
					popularProductsText={popularProductsText}
					popularSearchTermsText={popularSearchTermsText}
					searchResultProductsText={searchResultProductsText}
					searchResultsText={searchResultsText}
					searchResultsSpellingSuggestionText={
						searchResultsSpellingSuggestionText
					}
					searchFieldLabel={searchFieldLabel}
					searchNoResultsText={searchNoResultsText}
					searchStoreSuggestionLabel={searchStoreSuggestionLabel}
					searchSuggestionLabel={searchSuggestionLabel}
				/>
			</ErrorBoundary>
			<GlobalStoreSelectorButton
				onClick={onStoreSelectorClick}
				className="mx-4 -mt-1 mb-2 md:hidden"
				dataCy="global-select-store-small"
			/>

			{/* Ugly use of presentational div, but doing it properly on existing
			    elements got really clunky. */}
			<div
				className={cn(
					'border-b border-b-subtle transition-[border-color] md:hidden',
					isContainerStuck && 'headerRow:border-b-transparent',
				)}
			/>
		</>
	);
}
PageHeaderMain.displayName = 'PageHeaderMain';
